/**
 * Race a promise against a wall-clock cap. Resolves `{ timedOut: false, value }`
 * when the promise settles first, `{ timedOut: true }` when the cap fires
 * first. Rejections propagate to the caller.
 *
 * The raced promise is not cancelled. A caller whose late result holds a
 * resource (a MediaStream, a call object) must release it on arrival.
 *
 * Bounds the end-call teardown (CUS-1250): daily-js `leave()`/`destroy()`
 * have no internal timeout and can hang on a wedged call object. Bounds
 * microphone acquisition (GDN-3273): `getUserMedia` can hang on a Bluetooth
 * device the OS never hands over, freezing the recorder in "Starting…".
 */
export async function raceWithTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
): Promise<{ timedOut: false; value: T } | { timedOut: true }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<{ timedOut: true }>(resolve => {
    timer = setTimeout(() => resolve({ timedOut: true }), timeoutMs);
  });
  try {
    return await Promise.race([
      promise.then(value => ({ timedOut: false as const, value })),
      timeout,
    ]);
  } finally {
    clearTimeout(timer);
  }
}
